'use client';

import Card from '@/components/Card/Card';
import { FormControl, FormHelperText } from '@/components/field';
import InlineTextLink from '@/components/inlineTextLink/InlineTextLink';
import { RichText } from '@/components/richText/RichText';
import { SwdCheckbox } from '@/components/swdCheckbox/SwdCheckbox';
import { SwdFormControlLabel } from '@/components/swdFormControlLabel/SwdFormControlLabel';
import { getAssetUrl } from '@/helper/asset';
import { useOrderFormSettings } from '@/providers/OrderFormSettingsProvider';
import { useSettings } from '@/providers/SettingsProvider';
import { Form, useFormikContext } from 'formik';
import { type FC, useRef } from 'react';

import classes from './PageContractConclusion.module.scss';

interface PageContractConclusionFormProps {
  showMarketingOptIn: boolean;
  showEnfgCheck: boolean;
}

const PageContractConclusionForm: FC<PageContractConclusionFormProps> = ({ showMarketingOptIn, showEnfgCheck }) => {
  const settings = useSettings();
  const orderSettings = useOrderFormSettings();
  const refAcceptTerms = useRef<HTMLDivElement>(null);
  const refAcceptPrivacy = useRef<HTMLDivElement>(null);
  const refReceiveMarketing = useRef<HTMLDivElement>(null);
  const refEnfgCheck = useRef<HTMLDivElement>(null);

  const { errors, touched, values, handleChange, handleBlur } = useFormikContext<{
    acceptTerms: boolean;
    acceptPrivacy: boolean;
    receiveMarketing: boolean;
    enfgCheck: boolean;
  }>();

  return (
    <Card variant="Box" className={classes.checkboxCard}>
      <Form>
        <div className={classes.checkboxGroup}>
          <FormControl error={touched.acceptTerms && Boolean(errors.acceptTerms)}>
            <div ref={refAcceptTerms}>
              <SwdFormControlLabel
                control={
                  <SwdCheckbox
                    name="acceptTerms"
                    id="acceptTerms"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    checked={Boolean(values.acceptTerms)}
                    data-testid="CC-page-form-acceptTerms-checkbox"
                  />
                }
                label={<p>Ich bestätige die oben genannten Angaben.</p>}
              />
            </div>
            {touched.acceptTerms && Boolean(errors.acceptTerms) && (
              <FormHelperText>{errors.acceptTerms}</FormHelperText>
            )}
          </FormControl>
          <FormControl error={touched.acceptPrivacy && Boolean(errors.acceptPrivacy)}>
            <div ref={refAcceptPrivacy}>
              <SwdFormControlLabel
                control={
                  <SwdCheckbox
                    name="acceptPrivacy"
                    id="acceptPrivacy"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    checked={Boolean(values.acceptPrivacy)}
                    data-testid="CC-page-form-acceptPrivacy-checkbox"
                  />
                }
                label={
                  <p>
                    Mir ist bekannt, dass ich diesen Auftrag ohne Angabe von Gründen innerhalb von zwei Wochen in
                    Textform gegenüber der Stadtwerke Düsseldorf AG widerrufen kann. Ich habe die&nbsp;
                    <InlineTextLink href={getAssetUrl(orderSettings?.revocation_policy)} target="_blank">
                      Widerrufsbelehrung
                    </InlineTextLink>
                    &nbsp; zur Kenntnis genommen.
                  </p>
                }
              />
            </div>
            {touched.acceptPrivacy && Boolean(errors.acceptPrivacy) && (
              <FormHelperText>{errors.acceptPrivacy}</FormHelperText>
            )}
          </FormControl>
          {showMarketingOptIn && (
            <span>
              <FormControl>
                <div ref={refReceiveMarketing}>
                  <SwdFormControlLabel
                    control={
                      <SwdCheckbox
                        name="receiveMarketing"
                        id="receiveMarketing"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        checked={Boolean(values.receiveMarketing)}
                        data-testid="CC-page-form-receiveMarketing-checkbox"
                      />
                    }
                    label={<RichText text={settings?.cdc_marketing_optin_text} />}
                  />
                </div>
              </FormControl>
            </span>
          )}
          {showEnfgCheck && (
            <span>
              <FormControl error={touched.enfgCheck && Boolean(errors.enfgCheck)}>
                <div ref={refEnfgCheck}>
                  <SwdFormControlLabel
                    control={
                      <SwdCheckbox
                        name="enfgCheck"
                        id="enfgCheck"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        checked={Boolean(values.enfgCheck)}
                        data-testid="CC-page-form-enfgCheck-checkbox"
                      />
                    }
                    label={<RichText text={orderSettings?.enfg_check_text} />}
                  />
                </div>
                {touched.enfgCheck && Boolean(errors.enfgCheck) && <FormHelperText>{errors.enfgCheck}</FormHelperText>}
              </FormControl>
            </span>
          )}
        </div>
      </Form>
    </Card>
  );
};

export default PageContractConclusionForm;
